import { FC, useState } from 'react';
import { Mail } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { PunchupButton } from 'features/common/components/Punchup/primitives/buttons/PunchupButton';
import {
	PunchupDialogue,
	DialogIcon,
	DialogTitle,
	ButtonGroup,
} from 'features/common/components/Punchup/composites/dialogs/PunchupDialogue';
import { DateChangeEmailPreview, DateChangeEmailData } from './DateChangeEmailPreview';
import styles from './PunchupNotifyDateChangeDialog.module.css';

/** Email preview data minus the message, which the dialog owns and injects live from the textarea. */
export type DateChangeEmailPreviewData = Omit<DateChangeEmailData, 'customMessage'>;

export interface PunchupNotifyDateChangeDialogProps {
	preview: DateChangeEmailPreviewData;
	/** Confirm — notify attendees, forwarding the typed custom message (may be empty). */
	onConfirm: (customMessage: string) => void;
	/** Skip — save without notifying. */
	onSkip: () => void;
	isLoading?: boolean;
}

/**
 * Confirm dialog shown when a manager changes a show's date. Lets them add an optional message and see a
 * faithful preview of the exact email buyers will receive (the real template, rendered client-side) before
 * choosing to notify. Built on PunchupDialogue so it can host the textarea + preview that the props-only
 * PunchupConfirmDialog cannot. Owns its own customMessage state so both call sites stay thin. Render via
 * createPortal(..., document.body) — see PunchupDialogue invariant 1.
 */
export const PunchupNotifyDateChangeDialog: FC<PunchupNotifyDateChangeDialogProps> = ({
	preview,
	onConfirm,
	onSkip,
	isLoading = false,
}) => {
	const { t } = useTranslation();
	const [customMessage, setCustomMessage] = useState('');

	return (
		<PunchupDialogue maxWidth={560} isLoading={isLoading} onClose={isLoading ? undefined : onSkip}>
			<DialogIcon>
				<Mail size={24} />
			</DialogIcon>
			<DialogTitle>{t('editEvent-notify-title', 'Notify attendees of the new date?')}</DialogTitle>
			<p className={styles.text}>
				{t(
					'editEvent-notify-text',
					'You changed the show date. We can email everyone who already has a ticket to let them know.'
				)}
			</p>

			<label className="punchupFormLabel" htmlFor="notify-custom-message">
				{t('editEvent-notify-message-label', 'Add a message (optional)')}
			</label>
			<textarea
				id="notify-custom-message"
				className={styles.textarea}
				value={customMessage}
				onChange={(e) => setCustomMessage(e.target.value)}
				placeholder={t(
					'editEvent-notify-message-placeholder',
					'e.g. Sorry for the change — same great show, new night!'
				)}
				rows={3}
				disabled={isLoading}
			/>

			<p className={styles.previewLabel}>{t('editEvent-notify-preview-label', 'Preview')}</p>
			<DateChangeEmailPreview data={{ ...preview, customMessage: customMessage.trim() }} />

			<ButtonGroup>
				<PunchupButton variant="secondary" onClick={onSkip} disabled={isLoading}>
					{t('editEvent-notify-skip', 'Save without notifying')}
				</PunchupButton>
				<PunchupButton variant="primary" onClick={() => onConfirm(customMessage.trim())} disabled={isLoading}>
					{t('editEvent-notify-confirm', 'Notify attendees')}
				</PunchupButton>
			</ButtonGroup>
		</PunchupDialogue>
	);
};
